import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Label } from "../../../components/ui/label";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Switch } from "../../../components/ui/switch";
import { Textarea } from "../../../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../components/ui/select";
import { InputText } from "../../../components/InputText";
import { MilitarSelectField } from "../../../components/MilitarSelectField";
import {
  ArrowLeft, Save, Shield, User, Calendar, TrendingUp, Star,
  Trash2, Plus, Edit2, X, Check, Clock, ChevronDown, Pencil,
  Activity, LogIn, LogOut, RefreshCcw, Users
} from "lucide-react";
import { api } from "../../../services/api";
import { useAuth } from "../../../contexts/AuthContext";

// ---- tipos ----
interface Patente {
  id: string;
  patente: string;
  abreviacao: string;
  ordem: number;
  max_ordem_permitida: number;
}

interface Status {
  id: string;
  nome_status: string;
}

interface MilitarOpcao {
  id: string;
  nick: string;
  patente: string;
  nivel_patente: number;
}

interface Grupo {
  id: string;
  grupo: string;
}

interface GrupoCargo {
  id: string;
  cargo: string;
  id_grupo: string;
}

interface HistoricoPromocao {
  id: string;
  id_patente: string;
  patente_nome: string;
  patente_abreviacao: string;
  patente_ordem: number;
  id_militar_promoveu: string;
  nick_promotor: string | null;
  status: string;
  id_mensagem_discord: string;
  criado_em: string;
  alterado_em: string;
}

interface MilitarEdit {
  id: string;
  nick: string;
  id_patente: string;
  patente_nome: string;
  patente_ordem: number;
  id_militar_promoveu: string | null;
  nick_promotor: string | null;
  id_status: string;
  status_nome: string;
  id_sigla: string | null;
  sigla: string | null;
  ultima_promocao: string;
  criado_em: string;
  bio: string | null;
  banner: string | null;
  patrocinador: boolean;
  max_ordem_logado: number;
  historico_promocoes: HistoricoPromocao[];
  historico_status: HistoricoStatus[];
  historico_grupos: HistoricoGrupo[];
}

interface HistoricoStatus {
  id: string;
  id_status_anterior: string | null;
  status_anterior_nome: string | null;
  id_status_novo: string;
  status_novo_nome: string;
  id_militar_alterou: string | null;
  alterado_por_nick: string | null;
  motivo: string | null;
  criado_em: string;
}

interface HistoricoGrupo {
  id: string;
  tipo_evento: "ENTRADA" | "MUDANCA_CARGO" | "SAIDA";
  id_grupo: string;
  grupo_nome: string;
  cargo_anterior: string | null;
  cargo_novo: string | null;
  id_militar_responsavel: string | null;
  responsavel_nick: string | null;
  motivo: string | null;
  criado_em: string;
}

// ---- componente de linha do histórico ----
interface HistoricoRowProps {
  item: HistoricoPromocao;
  patentes: Patente[];
  militaresOpcoes: MilitarOpcao[];
  maxOrdemLogado: number;
  onSave: (id: string, data: Partial<HistoricoPromocao>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

function HistoricoRow({ item, patentes, militaresOpcoes, maxOrdemLogado, onSave, onDelete }: HistoricoRowProps) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    id_patente: item.id_patente,
    id_militar_promoveu: item.id_militar_promoveu,
    status: item.status,
    id_mensagem_discord: item.id_mensagem_discord ?? "",
    criado_em: moment(item.criado_em).format("YYYY-MM-DDTHH:mm"),
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(item.id, {
        ...form,
        criado_em: form.criado_em,
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const patentesPermitidas = patentes.filter(p => p.ordem <= maxOrdemLogado);

  if (!editing) {
    return (
      <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg text-sm">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex flex-col">
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {item.patente_abreviacao} – {item.patente_nome}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {item.nick_promotor ? `Promovido por: ${item.nick_promotor}` : "Promotor não registrado"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <Clock className="h-3 w-3" />
            {moment(item.criado_em).format("DD/MM/YYYY")}
          </div>
          <Badge
            className={
              item.status === "aprovado"
                ? "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300"
                : "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-gray-300"
            }
          >
            {item.status}
          </Badge>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditing(true)}>
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            onClick={() => onDelete(item.id)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 border border-blue-300 dark:border-blue-700 rounded-lg bg-blue-50/50 dark:bg-blue-900/10 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Patente</Label>
          <Select value={form.id_patente} onValueChange={(value) => setForm(prev => ({ ...prev, id_patente: value }))}>
            <SelectTrigger className="mt-1 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {patentesPermitidas.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.patente}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Promotor</Label>
          <MilitarSelectField
            value={form.id_militar_promoveu}
            onChange={(id) => setForm(prev => ({ ...prev, id_militar_promoveu: id }))}
            placeholder="Buscar militar..."
          />
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={form.status} onValueChange={(value) => setForm(prev => ({ ...prev, status: value }))}>
            <SelectTrigger className="mt-1 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="aprovado">Aprovado</SelectItem>
              <SelectItem value="pendente">Pendente</SelectItem>
              <SelectItem value="cancelado">Cancelado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Data</Label>
          <input
            type="datetime-local"
            value={form.criado_em}
            onChange={(e) => setForm(prev => ({ ...prev, criado_em: e.target.value }))}
            className="mt-1 flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">ID Mensagem Discord</Label>
          <InputText
            name="id_mensagem_discord"
            title=""
            value={form.id_mensagem_discord}
            onChange={(e) => setForm(prev => ({ ...prev, id_mensagem_discord: e.target.value }))}
            placeholder="ID da mensagem no Discord"
            className="mt-1 text-sm"
          />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
          <X className="h-4 w-4 mr-1" /> Cancelar
        </Button>
        <Button size="sm" disabled={saving} onClick={handleSave}
          className="bg-blue-600 hover:bg-blue-700 text-white">
          {saving ? "Salvando..." : <><Check className="h-4 w-4 mr-1" /> Salvar</>}
        </Button>
      </div>
    </div>
  );
}

// ---- componente de linha do histórico de status ----
interface HistoricoStatusRowProps {
  item: HistoricoStatus;
  statusList: Status[];
  militaresOpcoes: MilitarOpcao[];
  onSave: (id: string, data: Partial<HistoricoStatus>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

function HistoricoStatusRow({ item, statusList, militaresOpcoes, onSave, onDelete }: HistoricoStatusRowProps) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    id_status_anterior: item.id_status_anterior ?? "__none__",
    id_status_novo: item.id_status_novo,
    id_militar_alterou: "__none__",
    motivo: item.motivo ?? "",
    criado_em: moment(item.criado_em).format("YYYY-MM-DDTHH:mm"),
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(item.id, {
        ...form,
        id_status_anterior: form.id_status_anterior === "__none__" ? null : form.id_status_anterior,
        id_militar_alterou: form.id_militar_alterou === "__none__" ? undefined : form.id_militar_alterou,
        motivo: form.motivo || null,
        criado_em: form.criado_em,
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg text-sm">
        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
          <div className="flex items-center gap-2 font-medium text-gray-900 dark:text-gray-100">
            <span className="text-gray-400 dark:text-gray-500">{item.status_anterior_nome ?? "—"}</span>
            <span className="text-gray-400">→</span>
            <span className="text-amber-600 dark:text-amber-400">{item.status_novo_nome}</span>
          </div>
          {item.alterado_por_nick && (
            <span className="text-xs text-gray-500 dark:text-gray-400">Por: {item.alterado_por_nick}</span>
          )}
          {item.motivo && (
            <span className="text-xs italic text-gray-500 dark:text-gray-400">{item.motivo}</span>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <Clock className="h-3 w-3" />
            {moment(item.criado_em).format("DD/MM/YYYY")}
          </div>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditing(true)}>
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost" size="sm"
            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            onClick={() => onDelete(item.id)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 border border-amber-300 dark:border-amber-700 rounded-lg bg-amber-50/50 dark:bg-amber-900/10 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Status anterior</Label>
          <Select value={form.id_status_anterior} onValueChange={(value) => setForm(prev => ({ ...prev, id_status_anterior: value }))}>
            <SelectTrigger className="mt-1 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Nenhum —</SelectItem>
              {statusList.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.nome_status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Status novo *</Label>
          <Select value={form.id_status_novo} onValueChange={(value) => setForm(prev => ({ ...prev, id_status_novo: value }))}>
            <SelectTrigger className="mt-1 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Selecione —</SelectItem>
              {statusList.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.nome_status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Alterado por</Label>
          <MilitarSelectField
            value={form.id_militar_alterou ?? ""}
            onChange={(id) => setForm(prev => ({ ...prev, id_militar_alterou: id || null }))}
            placeholder="Buscar militar..."
          />
        </div>
        <div>
          <Label className="text-xs">Data</Label>
          <input
            type="datetime-local"
            value={form.criado_em}
            onChange={(e) => setForm(prev => ({ ...prev, criado_em: e.target.value }))}
            className="mt-1 flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
          />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Motivo</Label>
          <InputText
            name="motivo_status"
            title=""
            value={form.motivo}
            onChange={(e) => setForm(prev => ({ ...prev, motivo: e.target.value }))}
            placeholder="Motivo da alteração (opcional)"
            className="mt-1 text-sm"
          />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
          <X className="h-4 w-4 mr-1" /> Cancelar
        </Button>
        <Button size="sm" disabled={saving || !form.id_status_novo || form.id_status_novo === "__none__"} onClick={handleSave}
          className="bg-amber-600 hover:bg-amber-700 text-white">
          {saving ? "Salvando..." : <><Check className="h-4 w-4 mr-1" /> Salvar</>}
        </Button>
      </div>
    </div>
  );
}

// ---- componente de linha do histórico de grupos ----
interface HistoricoGrupoRowProps {
  item: HistoricoGrupo;
  grupos: Grupo[];
  militaresOpcoes: MilitarOpcao[];
  onSave: (id: string, data: Partial<HistoricoGrupo> & { id_grupo_cargo_anterior?: string | null; id_grupo_cargo_novo?: string | null }) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

function HistoricoGrupoRow({ item, grupos, militaresOpcoes, onSave, onDelete }: HistoricoGrupoRowProps) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cargos, setCargos] = useState<GrupoCargo[]>([]);
  const [form, setForm] = useState({
    id_grupo: item.id_grupo,
    tipo_evento: item.tipo_evento as string,
    id_grupo_cargo_anterior: "__none__",
    id_grupo_cargo_novo: "__none__",
    id_militar_responsavel: "__none__",
    motivo: item.motivo ?? "",
    criado_em: moment(item.criado_em).format("YYYY-MM-DDTHH:mm"),
  });

  useEffect(() => {
    if (editing && form.id_grupo) {
      api.get(`/grupos-cargos/grupo/${form.id_grupo}`)
        .then(res => setCargos(res.data))
        .catch(() => setCargos([]));
    }
  }, [editing, form.id_grupo]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(item.id, {
        ...form,
        tipo_evento: form.tipo_evento as HistoricoGrupo["tipo_evento"],
        id_grupo_cargo_anterior: form.id_grupo_cargo_anterior === "__none__" ? null : form.id_grupo_cargo_anterior,
        id_grupo_cargo_novo: form.id_grupo_cargo_novo === "__none__" ? null : form.id_grupo_cargo_novo,
        id_militar_responsavel: form.id_militar_responsavel === "__none__" ? undefined : form.id_militar_responsavel,
        motivo: form.motivo || null,
        criado_em: form.criado_em,
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const tipoLabels: Record<string, string> = {
    ENTRADA: "Entrada",
    MUDANCA_CARGO: "Mudança de Cargo",
    SAIDA: "Saída",
  };

  if (!editing) {
    return (
      <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg text-sm">
        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {item.tipo_evento === "ENTRADA" && (
              <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 text-xs">
                <LogIn className="h-3 w-3 mr-1" />Entrada
              </Badge>
            )}
            {item.tipo_evento === "MUDANCA_CARGO" && (
              <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 text-xs">
                <RefreshCcw className="h-3 w-3 mr-1" />Mudança de Cargo
              </Badge>
            )}
            {item.tipo_evento === "SAIDA" && (
              <Badge className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 text-xs">
                <LogOut className="h-3 w-3 mr-1" />Saída
              </Badge>
            )}
            <span className="font-medium text-gray-900 dark:text-gray-100">{item.grupo_nome}</span>
          </div>
          {(item.cargo_anterior || item.cargo_novo) && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {item.cargo_anterior && <>{item.cargo_anterior} → </>}
              {item.cargo_novo ?? "—"}
            </span>
          )}
          {item.responsavel_nick && (
            <span className="text-xs text-gray-500 dark:text-gray-400">Por: {item.responsavel_nick}</span>
          )}
          {item.motivo && (
            <span className="text-xs italic text-gray-500 dark:text-gray-400">{item.motivo}</span>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <Clock className="h-3 w-3" />
            {moment(item.criado_em).format("DD/MM/YYYY")}
          </div>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditing(true)}>
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost" size="sm"
            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            onClick={() => onDelete(item.id)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 border border-blue-300 dark:border-blue-700 rounded-lg bg-blue-50/50 dark:bg-blue-900/10 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Grupo *</Label>
          <Select value={form.id_grupo} onValueChange={(value) => {
            setForm(prev => ({ ...prev, id_grupo: value, id_grupo_cargo_anterior: "__none__", id_grupo_cargo_novo: "__none__" }));
            setCargos([]);
          }}>
            <SelectTrigger className="mt-1 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Selecione —</SelectItem>
              {grupos.map(g => (
                <SelectItem key={g.id} value={g.id}>{g.grupo}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Tipo de evento *</Label>
          <Select value={form.tipo_evento} onValueChange={(value) => setForm(prev => ({ ...prev, tipo_evento: value }))}>
            <SelectTrigger className="mt-1 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ENTRADA">Entrada</SelectItem>
              <SelectItem value="MUDANCA_CARGO">Mudança de Cargo</SelectItem>
              <SelectItem value="SAIDA">Saída</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Cargo anterior</Label>
          <Select value={form.id_grupo_cargo_anterior} onValueChange={(value) => setForm(prev => ({ ...prev, id_grupo_cargo_anterior: value }))}>
            <SelectTrigger className="mt-1 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Nenhum —</SelectItem>
              {cargos.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.cargo}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Responsável</Label>
          <MilitarSelectField
            value={form.id_militar_responsavel ?? ""}
            onChange={(id) => setForm(prev => ({ ...prev, id_militar_responsavel: id || null }))}
            placeholder="Buscar militar..."
          />
        </div>
        <div>
          <Label className="text-xs">Data</Label>
          <input
            type="datetime-local"
            value={form.criado_em}
            onChange={(e) => setForm(prev => ({ ...prev, criado_em: e.target.value }))}
            className="mt-1 flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
          />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Motivo</Label>
          <InputText
            name="motivo_grupo"
            title=""
            value={form.motivo}
            onChange={(e) => setForm(prev => ({ ...prev, motivo: e.target.value }))}
            placeholder="Motivo (opcional)"
            className="mt-1 text-sm"
          />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
          <X className="h-4 w-4 mr-1" /> Cancelar
        </Button>
        <Button size="sm" disabled={saving || !form.id_grupo || form.id_grupo === "__none__" || !form.tipo_evento} onClick={handleSave}
          className="bg-blue-600 hover:bg-blue-700 text-white">
          {saving ? "Salvando..." : <><Check className="h-4 w-4 mr-1" /> Salvar</>}
        </Button>
      </div>
    </div>
  );
}

// ---- página principal ----
export function EditarMilitar() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { militar: militarLogado } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<MilitarEdit | null>(null);
  const [patentes, setPatentes] = useState<Patente[]>([]);
  const [statusList, setStatusList] = useState<Status[]>([]);
  const [militaresOpcoes, setMilitaresOpcoes] = useState<MilitarOpcao[]>([]);
  const [grupos, setGrupos] = useState<Grupo[]>([]);

  // form state
  const [nick, setNick] = useState("");
  const [idPatente, setIdPatente] = useState("");
  const [idMilitarPromoveu, setIdMilitarPromoveu] = useState("");
  const [idStatus, setIdStatus] = useState("");
  const [ultimaPromocao, setUltimaPromocao] = useState("");
  const [criadoEm, setCriadoEm] = useState("");
  const [bio, setBio] = useState("");
  const [banner, setBanner] = useState("");
  const [patrocinador, setPatrocinador] = useState(false);

  // novo histórico
  const [showNovoHistorico, setShowNovoHistorico] = useState(false);
  const [novoHistorico, setNovoHistorico] = useState({
    id_patente: "__none__",
    id_militar_promoveu: "__none__",
    status: "aprovado",
    id_mensagem_discord: "",
    criado_em: moment().format("YYYY-MM-DDTHH:mm"),
  });
  const [savingNovoHistorico, setSavingNovoHistorico] = useState(false);

  // novo histórico de status
  const [showNovoHistoricoStatus, setShowNovoHistoricoStatus] = useState(false);
  const [novoHistoricoStatus, setNovoHistoricoStatus] = useState({
    id_status_anterior: "__none__",
    id_status_novo: "__none__",
    id_militar_alterou: "__none__",
    motivo: "",
    criado_em: moment().format("YYYY-MM-DDTHH:mm"),
  });
  const [savingNovoHistoricoStatus, setSavingNovoHistoricoStatus] = useState(false);

  // novo histórico de grupo
  const [showNovoHistoricoGrupo, setShowNovoHistoricoGrupo] = useState(false);
  const [novoHistoricoGrupo, setNovoHistoricoGrupo] = useState({
    id_grupo: "__none__",
    tipo_evento: "ENTRADA",
    id_grupo_cargo_anterior: "__none__",
    id_grupo_cargo_novo: "__none__",
    id_militar_responsavel: "__none__",
    motivo: "",
    criado_em: moment().format("YYYY-MM-DDTHH:mm"),
  });
  const [savingNovoHistoricoGrupo, setSavingNovoHistoricoGrupo] = useState(false);
  const [cargosNovoGrupo, setCargosNovoGrupo] = useState<GrupoCargo[]>([]);

  const maxOrdemLogado = militarLogado?.max_ordem_permitida ?? 0;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [editRes, patentesRes, statusRes, militaresRes, gruposRes] = await Promise.all([
        api.get(`/militares/${id}/edit`),
        api.get("/patentes"),
        api.get("/status"),
        api.get("/militares/listOficiais"),
        api.get("/grupos"),
      ]);

      const m: MilitarEdit = editRes.data;
      setData(m);
      setNick(m.nick);
      setIdPatente(m.id_patente);
      setIdMilitarPromoveu(m.id_militar_promoveu ?? "__none__");
      setIdStatus(m.id_status);
      setUltimaPromocao(m.ultima_promocao ? moment(m.ultima_promocao).format("YYYY-MM-DDTHH:mm") : "");
      setCriadoEm(m.criado_em ? moment(m.criado_em).format("YYYY-MM-DDTHH:mm") : "");
      setBio(m.bio ?? "");
      setBanner(m.banner ?? "");
      setPatrocinador(m.patrocinador ?? false);

      // Filtrar patentes que o logado pode atribuir
      const todasPatentes: Patente[] = patentesRes.data;
      setPatentes(todasPatentes.filter(p => p.ordem <= maxOrdemLogado));

      setStatusList(statusRes.data);

      const oficiais: any[] = militaresRes.data;
      setMilitaresOpcoes(
        oficiais.map(o => ({
          id: o.id,
          nick: o.nick,
          patente: o.patente?.patente ?? "",
          nivel_patente: o.patente?.ordem ?? 0,
        }))
      );

      const gruposData: any[] = gruposRes.data;
      setGrupos(gruposData.map(g => ({ id: g.id, grupo: g.grupo })));
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao carregar dados", { variant: "error" });
      navigate("/registros/militares");
    } finally {
      setLoading(false);
    }
  }, [id, maxOrdemLogado, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSalvarDados = async () => {
    setSaving(true);
    try {
      await api.patch(`/militares/${id}/edit`, {
        nick: nick || undefined,
        id_patente: idPatente || undefined,
        id_militar_promoveu: idMilitarPromoveu === "__none__" ? undefined : idMilitarPromoveu,
        id_status: idStatus || undefined,
        ultima_promocao: ultimaPromocao || undefined,
        criado_em: criadoEm || undefined,
        bio: bio || null,
        banner: banner || null,
        patrocinador,
      });
      enqueueSnackbar("Militar atualizado com sucesso!", { variant: "success" });
      await loadData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao salvar", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleSalvarHistorico = async (id_promocao: string, formData: Partial<HistoricoPromocao>) => {
    try {
      await api.patch(`/militares/historico-promocoes/${id_promocao}`, formData);
      enqueueSnackbar("Histórico atualizado!", { variant: "success" });
      await loadData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao atualizar histórico", { variant: "error" });
      throw error;
    }
  };

  const handleExcluirHistorico = async (id_promocao: string) => {
    if (!window.confirm("Deseja excluir este registro de promoção?")) return;
    try {
      await api.delete(`/militares/historico-promocoes/${id_promocao}`);
      enqueueSnackbar("Registro excluído!", { variant: "success" });
      await loadData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao excluir", { variant: "error" });
    }
  };

  const handleExcluirHistoricoStatus = async (id_historico: string) => {
    if (!window.confirm("Deseja excluir este registro de status?")) return;
    try {
      await api.delete(`/militares/historico-status/${id_historico}`);
      enqueueSnackbar("Registro excluído!", { variant: "success" });
      await loadData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao excluir", { variant: "error" });
    }
  };

  const handleExcluirHistoricoGrupo = async (id_historico: string) => {
    if (!window.confirm("Deseja excluir este registro de grupo?")) return;
    try {
      await api.delete(`/militares/historico-grupos/${id_historico}`);
      enqueueSnackbar("Registro excluído!", { variant: "success" });
      await loadData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao excluir", { variant: "error" });
    }
  };

  const handleSalvarHistoricoStatus = async (id_historico: string, formData: Partial<HistoricoStatus>) => {
    try {
      await api.patch(`/militares/historico-status/${id_historico}`, formData);
      enqueueSnackbar("Histórico de status atualizado!", { variant: "success" });
      await loadData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao atualizar", { variant: "error" });
      throw error;
    }
  };

  const handleAdicionarHistoricoStatus = async () => {
    setSavingNovoHistoricoStatus(true);
    try {
      await api.post(`/militares/${id}/historico-status`, {
        ...novoHistoricoStatus,
        id_status_anterior: novoHistoricoStatus.id_status_anterior === "__none__" ? null : novoHistoricoStatus.id_status_anterior,
        id_militar_alterou: novoHistoricoStatus.id_militar_alterou === "__none__" ? null : novoHistoricoStatus.id_militar_alterou,
        motivo: novoHistoricoStatus.motivo || null,
      });
      enqueueSnackbar("Histórico de status adicionado!", { variant: "success" });
      setShowNovoHistoricoStatus(false);
      setNovoHistoricoStatus({ id_status_anterior: "__none__", id_status_novo: "__none__", id_militar_alterou: "__none__", motivo: "", criado_em: moment().format("YYYY-MM-DDTHH:mm") });
      await loadData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao adicionar", { variant: "error" });
    } finally {
      setSavingNovoHistoricoStatus(false);
    }
  };

  const handleSalvarHistoricoGrupo = async (id_historico: string, formData: Partial<HistoricoGrupo> & { id_grupo_cargo_anterior?: string | null; id_grupo_cargo_novo?: string | null }) => {
    try {
      await api.patch(`/militares/historico-grupos/${id_historico}`, formData);
      enqueueSnackbar("Histórico de grupo atualizado!", { variant: "success" });
      await loadData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao atualizar", { variant: "error" });
      throw error;
    }
  };

  const handleAdicionarHistoricoGrupo = async () => {
    setSavingNovoHistoricoGrupo(true);
    try {
      await api.post(`/militares/${id}/historico-grupos`, {
        ...novoHistoricoGrupo,
        id_grupo_cargo_anterior: novoHistoricoGrupo.id_grupo_cargo_anterior === "__none__" ? null : novoHistoricoGrupo.id_grupo_cargo_anterior,
        id_grupo_cargo_novo: novoHistoricoGrupo.id_grupo_cargo_novo === "__none__" ? null : novoHistoricoGrupo.id_grupo_cargo_novo,
        id_militar_responsavel: novoHistoricoGrupo.id_militar_responsavel === "__none__" ? null : novoHistoricoGrupo.id_militar_responsavel,
        motivo: novoHistoricoGrupo.motivo || null,
      });
      enqueueSnackbar("Histórico de grupo adicionado!", { variant: "success" });
      setShowNovoHistoricoGrupo(false);
      setNovoHistoricoGrupo({ id_grupo: "__none__", tipo_evento: "ENTRADA", id_grupo_cargo_anterior: "__none__", id_grupo_cargo_novo: "__none__", id_militar_responsavel: "__none__", motivo: "", criado_em: moment().format("YYYY-MM-DDTHH:mm") });
      setCargosNovoGrupo([]);
      await loadData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao adicionar", { variant: "error" });
    } finally {
      setSavingNovoHistoricoGrupo(false);
    }
  };

  const handleAdicionarHistorico = async () => {
    setSavingNovoHistorico(true);
    try {
      await api.post(`/militares/${id}/historico-promocoes`, {
        ...novoHistorico,
        id_patente: novoHistorico.id_patente === "__none__" ? undefined : novoHistorico.id_patente,
        id_militar_promoveu: novoHistorico.id_militar_promoveu === "__none__" ? undefined : novoHistorico.id_militar_promoveu,
        criado_em: novoHistorico.criado_em,
      });
      enqueueSnackbar("Histórico adicionado!", { variant: "success" });
      setShowNovoHistorico(false);
      setNovoHistorico({
        id_patente: "__none__",
        id_militar_promoveu: "__none__",
        status: "aprovado",
        id_mensagem_discord: "",
        criado_em: moment().format("YYYY-MM-DDTHH:mm"),
      });
      await loadData();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao adicionar histórico", { variant: "error" });
    } finally {
      setSavingNovoHistorico(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-gray-500 dark:text-gray-400">Carregando...</div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4 border-b border-border pb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/registros/militares")}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Voltar
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Pencil className="h-6 w-6 text-primary" />
            Editar Militar
          </h1>
          <p className="text-muted-foreground mt-1">
            Editando: <strong>{data.nick}</strong> — {data.patente_nome}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna principal */}
        <div className="lg:col-span-2 space-y-6">

          {/* Card de dados */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)] dark:text-[oklch(0.55_0.15_142)]">
                <User className="h-5 w-5" />
                Dados do Militar
              </CardTitle>
              <CardDescription>Edite os dados cadastrais do militar</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Nick */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nick">Nick</Label>
                  <InputText
                    id="nick"
                    name="nick"
                    title=""
                    value={nick}
                    onChange={(e) => setNick(e.target.value)}
                    placeholder="Nick do militar"
                    className="mt-1"
                  />
                </div>

                {/* Status */}
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select value={idStatus} onValueChange={setIdStatus}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Selecione —</SelectItem>
                      {statusList.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.nome_status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Patente */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="patente">Patente</Label>
                  <Select value={idPatente} onValueChange={setIdPatente}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Selecione —</SelectItem>
                      {patentes.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.patente}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {data.patente_ordem > maxOrdemLogado && (
                    <p className="text-xs text-amber-600 mt-1">
                      A patente atual excede sua permissão — você pode apenas rebaixar.
                    </p>
                  )}
                </div>

                {/* Promotor */}
                <div>
                  <Label htmlFor="promotor">Militar que promoveu</Label>
                  <Select value={idMilitarPromoveu} onValueChange={setIdMilitarPromoveu}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Selecione —</SelectItem>
                      {militaresOpcoes.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.nick}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Datas */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="criado_em">Data do Alistamento</Label>
                  <input
                    id="criado_em"
                    type="datetime-local"
                    value={criadoEm}
                    onChange={(e) => setCriadoEm(e.target.value)}
                    className="mt-1 flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                  />
                </div>
                <div>
                  <Label htmlFor="ultima_promocao">Data da Última Promoção</Label>
                  <input
                    id="ultima_promocao"
                    type="datetime-local"
                    value={ultimaPromocao}
                    onChange={(e) => setUltimaPromocao(e.target.value)}
                    className="mt-1 flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                  />
                </div>
              </div>

              {/* Patrocinador */}
              <div className="flex items-center gap-3">
                <Switch
                  id="patrocinador"
                  checked={patrocinador}
                  onCheckedChange={setPatrocinador}
                />
                <Label htmlFor="patrocinador" className="cursor-pointer flex items-center gap-1">
                  <Star className="h-4 w-4 text-amber-500" />
                  Patrocinador
                </Label>
              </div>

              {/* Banner */}
              <div>
                <Label htmlFor="banner">URL do Banner</Label>
                <InputText
                  id="banner"
                  name="banner"
                  title=""
                  value={banner}
                  onChange={(e) => setBanner(e.target.value)}
                  placeholder="https://..."
                  className="mt-1"
                />
                {banner && (
                  <div className="mt-2 rounded-md overflow-hidden border border-border">
                    <img src={banner} alt="Banner" className="w-full h-24 object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  </div>
                )}
              </div>

              {/* Bio */}
              <div>
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Biografia do militar..."
                  className="mt-1 dark:bg-background dark:text-foreground dark:border-border"
                  rows={4}
                />
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleSalvarDados}
                  disabled={saving}
                  className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                >
                  {saving ? "Salvando..." : <><Save className="h-4 w-4 mr-2" />Salvar Dados</>}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Card histórico de promoções */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)] dark:text-[oklch(0.55_0.15_142)]">
                    <TrendingUp className="h-5 w-5" />
                    Histórico de Promoções
                  </CardTitle>
                  <CardDescription>
                    {data.historico_promocoes.length} registro(s) encontrado(s)
                  </CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowNovoHistorico(v => !v)}
                  className="text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/30"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Formulário novo histórico */}
              {showNovoHistorico && (
                <div className="p-3 border border-green-300 dark:border-green-700 rounded-lg bg-green-50/50 dark:bg-green-900/10 space-y-3">
                  <h4 className="text-sm font-medium text-green-700 dark:text-green-400">Novo registro</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Patente</Label>
                      <Select value={novoHistorico.id_patente} onValueChange={(value) => setNovoHistorico(prev => ({ ...prev, id_patente: value }))}>
                        <SelectTrigger className="mt-1 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Selecione —</SelectItem>
                          {patentes.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.patente}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Promotor</Label>
                      <MilitarSelectField
                        value={novoHistorico.id_militar_promoveu}
                        onChange={(id) => setNovoHistorico(prev => ({ ...prev, id_militar_promoveu: id }))}
                        placeholder="Buscar militar..."
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Status</Label>
                      <Select value={novoHistorico.status} onValueChange={(value) => setNovoHistorico(prev => ({ ...prev, status: value }))}>
                        <SelectTrigger className="mt-1 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="aprovado">Aprovado</SelectItem>
                          <SelectItem value="pendente">Pendente</SelectItem>
                          <SelectItem value="cancelado">Cancelado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Data</Label>
                      <input
                        type="datetime-local"
                        value={novoHistorico.criado_em}
                        onChange={(e) => setNovoHistorico(prev => ({ ...prev, criado_em: e.target.value }))}
                        className="mt-1 flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">ID Mensagem Discord</Label>
                      <InputText
                        name="id_mensagem_discord_novo"
                        title=""
                        value={novoHistorico.id_mensagem_discord}
                        onChange={(e) => setNovoHistorico(prev => ({ ...prev, id_mensagem_discord: e.target.value }))}
                        placeholder="ID da mensagem no Discord (opcional)"
                        className="mt-1 text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => setShowNovoHistorico(false)}>
                      <X className="h-4 w-4 mr-1" /> Cancelar
                    </Button>
                    <Button
                      size="sm"
                      disabled={savingNovoHistorico || !novoHistorico.id_patente || novoHistorico.id_patente === "__none__"}
                      onClick={handleAdicionarHistorico}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {savingNovoHistorico ? "Salvando..." : <><Check className="h-4 w-4 mr-1" />Adicionar</>}
                    </Button>
                  </div>
                </div>
              )}

              {data.historico_promocoes.length === 0 ? (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
                  Nenhum histórico de promoção registrado
                </div>
              ) : (
                data.historico_promocoes.map((item) => (
                  <HistoricoRow
                    key={item.id}
                    item={item}
                    patentes={patentes}
                    militaresOpcoes={militaresOpcoes}
                    maxOrdemLogado={maxOrdemLogado}
                    onSave={handleSalvarHistorico}
                    onDelete={handleExcluirHistorico}
                  />
                ))
              )}
            </CardContent>
          </Card>

          {/* Card histórico de status */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)] dark:text-[oklch(0.55_0.15_142)]">
                    <Activity className="h-5 w-5" />
                    Histórico de Status
                  </CardTitle>
                  <CardDescription>
                    {data.historico_status.length} registro(s) encontrado(s)
                  </CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowNovoHistoricoStatus(v => !v)}
                  className="text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/30"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Formulário novo histórico de status */}
              {showNovoHistoricoStatus && (
                <div className="p-3 border border-green-300 dark:border-green-700 rounded-lg bg-green-50/50 dark:bg-green-900/10 space-y-3">
                  <h4 className="text-sm font-medium text-green-700 dark:text-green-400">Novo registro</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Status anterior</Label>
                      <Select value={novoHistoricoStatus.id_status_anterior} onValueChange={(value) => setNovoHistoricoStatus(prev => ({ ...prev, id_status_anterior: value }))}>
                        <SelectTrigger className="mt-1 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Nenhum —</SelectItem>
                          {statusList.map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.nome_status}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Status novo *</Label>
                      <Select value={novoHistoricoStatus.id_status_novo} onValueChange={(value) => setNovoHistoricoStatus(prev => ({ ...prev, id_status_novo: value }))}>
                        <SelectTrigger className="mt-1 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Selecione —</SelectItem>
                          {statusList.map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.nome_status}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Alterado por</Label>
                      <MilitarSelectField
                        value={novoHistoricoStatus.id_militar_alterou}
                        onChange={(id) => setNovoHistoricoStatus(prev => ({ ...prev, id_militar_alterou: id }))}
                        placeholder="Buscar militar..."
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Data</Label>
                      <input
                        type="datetime-local"
                        value={novoHistoricoStatus.criado_em}
                        onChange={(e) => setNovoHistoricoStatus(prev => ({ ...prev, criado_em: e.target.value }))}
                        className="mt-1 flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Motivo</Label>
                      <InputText
                        name="motivo_status_novo"
                        title=""
                        value={novoHistoricoStatus.motivo}
                        onChange={(e) => setNovoHistoricoStatus(prev => ({ ...prev, motivo: e.target.value }))}
                        placeholder="Motivo da alteração (opcional)"
                        className="mt-1 text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => setShowNovoHistoricoStatus(false)}>
                      <X className="h-4 w-4 mr-1" /> Cancelar
                    </Button>
                    <Button
                      size="sm"
                      disabled={savingNovoHistoricoStatus || !novoHistoricoStatus.id_status_novo || novoHistoricoStatus.id_status_novo === "__none__"}
                      onClick={handleAdicionarHistoricoStatus}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {savingNovoHistoricoStatus ? "Salvando..." : <><Check className="h-4 w-4 mr-1" />Adicionar</>}
                    </Button>
                  </div>
                </div>
              )}

              {data.historico_status.length === 0 && !showNovoHistoricoStatus ? (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
                  Nenhum histórico de status registrado
                </div>
              ) : (
                data.historico_status.map((item) => (
                  <HistoricoStatusRow
                    key={item.id}
                    item={item}
                    statusList={statusList}
                    militaresOpcoes={militaresOpcoes}
                    onSave={handleSalvarHistoricoStatus}
                    onDelete={handleExcluirHistoricoStatus}
                  />
                ))
              )}
            </CardContent>
          </Card>

          {/* Card histórico de grupos */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)] dark:text-[oklch(0.55_0.15_142)]">
                    <Users className="h-5 w-5" />
                    Histórico de Grupos
                  </CardTitle>
                  <CardDescription>
                    {data.historico_grupos.length} registro(s) encontrado(s)
                  </CardDescription>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowNovoHistoricoGrupo(v => !v)}
                  className="text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/30"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Formulário novo histórico de grupo */}
              {showNovoHistoricoGrupo && (
                <div className="p-3 border border-green-300 dark:border-green-700 rounded-lg bg-green-50/50 dark:bg-green-900/10 space-y-3">
                  <h4 className="text-sm font-medium text-green-700 dark:text-green-400">Novo registro</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Grupo *</Label>
                      <Select value={novoHistoricoGrupo.id_grupo} onValueChange={(value) => {
                        setNovoHistoricoGrupo(prev => ({ ...prev, id_grupo: value, id_grupo_cargo_anterior: "", id_grupo_cargo_novo: "" }));
                        setCargosNovoGrupo([]);
                        if (value) {
                          api.get(`/grupos-cargos/grupo/${value}`)
                            .then(res => setCargosNovoGrupo(res.data))
                            .catch(() => setCargosNovoGrupo([]));
                        }
                      }}>
                        <SelectTrigger className="mt-1 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Selecione —</SelectItem>
                          {grupos.map(g => (
                            <SelectItem key={g.id} value={g.id}>{g.grupo}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Tipo de evento *</Label>
                      <Select value={novoHistoricoGrupo.tipo_evento} onValueChange={(value) => setNovoHistoricoGrupo(prev => ({ ...prev, tipo_evento: value }))}>
                        <SelectTrigger className="mt-1 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ENTRADA">Entrada</SelectItem>
                          <SelectItem value="MUDANCA_CARGO">Mudança de Cargo</SelectItem>
                          <SelectItem value="SAIDA">Saída</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Cargo anterior</Label>
                      <Select value={novoHistoricoGrupo.id_grupo_cargo_anterior} onValueChange={(value) => setNovoHistoricoGrupo(prev => ({ ...prev, id_grupo_cargo_anterior: value }))}>
                        <SelectTrigger className="mt-1 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Nenhum —</SelectItem>
                          {cargosNovoGrupo.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.cargo}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Cargo novo</Label>
                      <Select value={novoHistoricoGrupo.id_grupo_cargo_novo} onValueChange={(value) => setNovoHistoricoGrupo(prev => ({ ...prev, id_grupo_cargo_novo: value }))}>
                        <SelectTrigger className="mt-1 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Nenhum —</SelectItem>
                          {cargosNovoGrupo.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.cargo}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Responsável</Label>
                      <MilitarSelectField
                        value={novoHistoricoGrupo.id_militar_responsavel}
                        onChange={(id) => setNovoHistoricoGrupo(prev => ({ ...prev, id_militar_responsavel: id }))}
                        placeholder="Buscar militar..."
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Data</Label>
                      <input
                        type="datetime-local"
                        value={novoHistoricoGrupo.criado_em}
                        onChange={(e) => setNovoHistoricoGrupo(prev => ({ ...prev, criado_em: e.target.value }))}
                        className="mt-1 flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Motivo</Label>
                      <InputText
                        name="motivo_grupo_novo"
                        title=""
                        value={novoHistoricoGrupo.motivo}
                        onChange={(e) => setNovoHistoricoGrupo(prev => ({ ...prev, motivo: e.target.value }))}
                        placeholder="Motivo (opcional)"
                        className="mt-1 text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => setShowNovoHistoricoGrupo(false)}>
                      <X className="h-4 w-4 mr-1" /> Cancelar
                    </Button>
                    <Button
                      size="sm"
                      disabled={savingNovoHistoricoGrupo || !novoHistoricoGrupo.id_grupo || novoHistoricoGrupo.id_grupo === "__none__" || !novoHistoricoGrupo.tipo_evento}
                      onClick={handleAdicionarHistoricoGrupo}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {savingNovoHistoricoGrupo ? "Salvando..." : <><Check className="h-4 w-4 mr-1" />Adicionar</>}
                    </Button>
                  </div>
                </div>
              )}

              {data.historico_grupos.length === 0 && !showNovoHistoricoGrupo ? (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
                  Nenhum histórico de grupo registrado
                </div>
              ) : (
                data.historico_grupos.map((item) => (
                  <HistoricoGrupoRow
                    key={item.id}
                    item={item}
                    grupos={grupos}
                    militaresOpcoes={militaresOpcoes}
                    onSave={handleSalvarHistoricoGrupo}
                    onDelete={handleExcluirHistoricoGrupo}
                  />
                ))
              )}
            </CardContent>
          </Card>

        </div>

        {/* Painel lateral */}
        <div className="space-y-6">
          {/* Avatar */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <CardTitle className="text-sm text-[oklch(0.35_0.15_142)] dark:text-[oklch(0.55_0.15_142)]">
                Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center gap-3">
              <div className="w-24 h-24 rounded-full bg-[oklch(0.45_0.15_142)]/10 overflow-hidden flex items-center justify-center border-2 border-[oklch(0.45_0.15_142)]/30">
                <img
                  src={`http://www.habbo.com.br/habbo-imaging/avatarimage?&user=${nick}&action=std&direction=2&head_direction=2&img_format=png&gesture=std&headonly=0&size=l`}
                  alt={nick}
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
              <div className="text-center">
                <p className="font-semibold text-gray-900 dark:text-gray-100">{nick}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {patentes.find(p => p.id === idPatente)?.patente ?? data.patente_nome}
                </p>
                {patrocinador && (
                  <Badge className="mt-1 bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300">
                    <Star className="h-3 w-3 mr-1" /> Patrocinador
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Resumo */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Informações
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">ID</span>
                <span className="font-mono text-xs">{data.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Promoções</span>
                <Badge variant="outline">{data.historico_promocoes.length}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <Badge variant="outline">{data.historico_status.length}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Grupos</span>
                <Badge variant="outline">{data.historico_grupos.length}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <Badge variant="outline">{data.historico_status.length}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Grupos</span>
                <Badge variant="outline">{data.historico_grupos.length}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Alistamento</span>
                <span>{moment(data.criado_em).format("DD/MM/YYYY")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Última promoção</span>
                <span>{moment(data.ultima_promocao).format("DD/MM/YYYY")}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
